/**
 * The consultant's correction and Record-as-answer cards (L2 of the 2026-10-01 learning-loop plan;
 * docs/design/2026-10-02_recommendation-corrections.md), in words, and what a person's click sends. The consultant
 * records nothing: a correction card carries a recommendation's key and the person's reason, quoted from their own
 * words, and "Record this correction" posts the event as them (via copilot:<thread>); a plant-answer card carries their
 * words for an open question, which they may edit, and "Record as answer" posts them to the question's own route. Pure.
 */
import type { CopilotCorrectionBlock, CopilotPlantAnswerBlock } from "@/lib/api/copilot-imports";
import type { RecommendationEventCreate } from "@/lib/api/recommendations";
import { clampText, stateLine } from "@/lib/recommendations/words";

/** The longest reason an event keeps, and the longest answer a question takes. */
const REASON_MAX = 2000;
const ANSWER_MAX = 8000;

/** Where a correction recorded from a thread says it came from. */
export function copilotVia(threadId: string): string {
  return `copilot:${threadId}`;
}

/** "Marked wrong", "Does not apply here": which verdict a card records. */
export function correctionKindWords(kind: CopilotCorrectionBlock["kind"]): string {
  return kind === "wrong" ? "Marked wrong" : "Does not apply here";
}

/**
 * Whether a correction card was recorded from this thread: its key's latest event is the card's verdict, recorded via
 * this thread. A later event elsewhere (reopened on the brief, say) means it no longer stands as recorded here. A
 * thread shown read-only names none (threadId null): what the card says is the key's latest event, as it is now.
 */
export function correctionRecorded(block: CopilotCorrectionBlock, threadId: string | null): boolean {
  const s = block.state;
  return threadId !== null && !!s && s.kind === block.kind && s.via === copilotVia(threadId);
}

/**
 * The card's state line: "Recorded as yours: Marked wrong by Asha on 5 Aug 2026: ..." once recorded from this thread;
 * the recommendation's latest event from elsewhere ("Tried on 3 Aug 2026 by ..."), as the brief shows it; or nothing.
 */
export function correctionStateLine(block: CopilotCorrectionBlock, threadId: string | null): string | null {
  if (!block.state) return null;
  const line = stateLine(block.state);
  return correctionRecorded(block, threadId) ? `Recorded: ${line}` : `Now: ${line}`;
}

/** The request "Record this correction" sends, as the person, or why it cannot go. */
export function correctionRequest(
  block: CopilotCorrectionBlock,
  threadId: string,
  reason: string,
): { body: RecommendationEventCreate; problem?: undefined } | { body?: undefined; problem: string } {
  const note = reason.trim();
  if (!note) return { problem: "Say why, in your words: the next brief and the consultant take it into account." };
  if ([...note].length > REASON_MAX) return { problem: `Keep the reason under ${REASON_MAX} characters.` };
  return {
    body: {
      key: block.key,
      text_shown: clampText(block.text?.trim() || block.key),
      kind: block.kind,
      note,
      via: copilotVia(threadId),
    },
  };
}

/** What the card says when the record went and only reading the conversation again failed. */
export const RECORDED_NOT_SHOWN = "It was recorded, but this page could not read the conversation again. Reload the page to see it.";

/**
 * Records a card, then reads the thread again, so the card shows what was recorded. Resolves to nothing when both went,
 * or to what the card says: the record's own refusal (refusalText: it was not recorded; the thread is not read again),
 * or RECORDED_NOT_SHOWN when the record went and only the read-again failed: a person who is told it was not recorded
 * records it twice.
 */
export async function recordThenReload(
  send: () => Promise<unknown>,
  reload: () => Promise<unknown>,
  refusalText: (err: unknown) => string,
): Promise<string | null> {
  try {
    await send();
  } catch (err) {
    return refusalText(err);
  }
  try {
    await reload();
  } catch {
    return RECORDED_NOT_SHOWN;
  }
  return null;
}

/** A plant-answer card's state line: answered (and when), closed or withdrawn, from the question; else nothing. */
export function plantAnswerStateLine(block: CopilotPlantAnswerBlock): string | null {
  switch (block.status) {
    case "answered":
      return "Recorded as the plant's answer.";
    case "closed":
      return "The question is closed.";
    case "withdrawn":
      return "The question was withdrawn.";
    default:
      return null;
  }
}

/** Whether a plant-answer card can still record: its question is open to an answer. */
export function plantAnswerOpen(block: CopilotPlantAnswerBlock): boolean {
  return block.status === undefined || block.status === "asked";
}

/** The answer "Record as answer" sends, or why it cannot go. */
export function plantAnswerRequest(text: string): { body: { text: string }; problem?: undefined } | { body?: undefined; problem: string } {
  const t = text.trim();
  if (!t) return { problem: "Write the answer first." };
  if ([...t].length > ANSWER_MAX) return { problem: `Keep the answer under ${ANSWER_MAX} characters.` };
  return { body: { text: t } };
}
